import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "@/hooks/use-toast";

export interface ProfessorComment {
  id: string;
  professor_id: string;
  member_id: string;
  schedule_id: string | null;
  gym_id: string;
  content: string;
  created_at: string;
  updated_at: string;
  professor?: {
    first_name: string | null;
    last_name: string | null;
    avatar_url: string | null;
  };
}

export const useProfessorComments = (memberId?: string, gymId?: string | null) => {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const commentsQuery = useQuery({
    queryKey: ["professor-comments", memberId, gymId],
    queryFn: async (): Promise<ProfessorComment[]> => {
      const { data, error } = await supabase
        .from("professor_comments")
        .select("*")
        .eq("member_id", memberId!)
        .order("created_at", { ascending: false });

      if (error) throw error;

      const profIds = [...new Set((data || []).map(c => c.professor_id))];
      if (profIds.length > 0) {
        const { data: profiles } = await supabase
          .from("profiles")
          .select("id, first_name, last_name, avatar_url")
          .in("id", profIds);
        const profMap = new Map((profiles || []).map(p => [p.id, p]));
        return (data || []).map(c => ({
          ...c,
          professor: profMap.get(c.professor_id) || undefined,
        }));
      }

      return data || [];
    },
    enabled: !!memberId && !!gymId,
  });

  const addComment = useMutation({
    mutationFn: async (data: { member_id: string; gym_id: string; content: string; schedule_id?: string }) => {
      const { error } = await supabase
        .from("professor_comments")
        .insert({
          professor_id: user!.id,
          ...data,
        });
      if (error) throw error;
    },
    onSuccess: () => {
      toast({ title: "Comentario agregado" });
      queryClient.invalidateQueries({ queryKey: ["professor-comments"] });
    },
    onError: () => {
      toast({ title: "Error al agregar comentario", variant: "destructive" });
    },
  });

  return {
    comments: commentsQuery.data || [],
    isLoading: commentsQuery.isLoading,
    addComment,
  };
};
